import { useMutation } from "@tanstack/react-query";
import { getControlName, service, useAuthConfig } from "@theprelude/core";
import {
  Button,
  HelpIcon,
  notifyError,
  notifySuccess,
  MicrosoftEntraIcon,
} from "@theprelude/ds";
import { useOverlay } from "@/hooks/use-overlay";
import {
  SupportedMicrosoftControl,
  useConnectIntegrationViaAzure,
} from "@/lib/azure";
import { DefaultTooltip } from "../default-tooltip";

type ControlDefinition = {
  controlCode: SupportedMicrosoftControl;
  instanceId: string;
  instanceName: string;
  baseUrl: string;
};

export const ConnectWithEntraIdButton = ({
  controls,
  onSuccess,
}: {
  controls: ControlDefinition[];
  onSuccess?: (status: {
    requiredAdminConsent: boolean;
    requiredGlobalReaderRoleAssignment: boolean;
    appId: string;
  }) => void;
}) => {
  const connectIntegrationViaAzure = useConnectIntegrationViaAzure();
  const { credentials } = useAuthConfig();
  const overlay = useOverlay();
  const authConfig = useAuthConfig();

  const attachPartner = useMutation({
    retry: 10,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
    mutationFn: async ({
      tenantId,
      appId,
      appSecret,
      control,
    }: {
      control: ControlDefinition;
      tenantId: string;
      appId: string;
      appSecret: string;
    }) => {
      const { controlCode, instanceId, instanceName, baseUrl } = control;
      await service(authConfig).partner.attachPartner({
        partnerCode: controlCode,
        instanceId,
        name: instanceName,
        api: baseUrl,
        user: tenantId,
        secret: `${appId}/${appSecret}`,
      });
    },

    onError: (error, { control }) => {
      notifyError({
        message: `Failed to connect to ${getControlName(control.controlCode)}.`,
        error,
      });
    },
  });

  return (
    <div className="flex flex-col gap-3">
      <Button
        intent="secondary"
        icon={<MicrosoftEntraIcon />}
        disabled={
          connectIntegrationViaAzure.isPending || attachPartner.isPending
        }
        loading={
          connectIntegrationViaAzure.isPending || attachPartner.isPending
        }
        onClick={() => {
          connectIntegrationViaAzure.mutate(
            {
              controls: controls.map((control) => control.controlCode),
              accountId: credentials?.account ?? "",
            },
            {
              onSuccess: async ({
                appId,
                clientSecret,
                tenantId,
                requiredAdminConsent,
                requiredGlobalReaderRoleAssignment,
              }) => {
                for (const control of controls) {
                  await attachPartner.mutateAsync({
                    appId,
                    appSecret: clientSecret,
                    tenantId,
                    control,
                  });
                }

                if (onSuccess) {
                  onSuccess({
                    requiredAdminConsent,
                    requiredGlobalReaderRoleAssignment,
                    appId,
                  });
                } else {
                  notifySuccess({
                    duration:
                      requiredAdminConsent || requiredGlobalReaderRoleAssignment
                        ? Number.POSITIVE_INFINITY
                        : undefined,
                    dismissible: true,
                    message: `${controls
                      .map((control) => getControlName(control.controlCode))
                      .join(", ")} successfully added.${
                      requiredAdminConsent
                        ? " You need to grant admin consent to the app on the Azure portal."
                        : ""
                    }${
                      requiredGlobalReaderRoleAssignment
                        ? " Global reader role must be added manually to app registration."
                        : ""
                    }`,
                  });
                  void overlay.close();
                }
              },
              onError: (err) => {
                if (err.message.includes("user_cancelled")) {
                  return;
                }

                notifyError({
                  message: `Failed to connect to Microsoft Entra ID.`,
                  error: err,
                });
              },
            },
          );
        }}
      >
        Configure integration with your Microsoft Entra ID
      </Button>

      <div className="flex w-fit flex-col gap-2">
        <DefaultTooltip content={<RequiredRoles />}>
          <a
            href="https://support.preludesecurity.com/microsoft-automated-integration-setup"
            target="_blank"
            rel="noopener noreferrer"
            className="mb-2 flex cursor-pointer flex-row items-center justify-start gap-2 text-sm text-primary-foreground underline underline-offset-2 font-normal"
          >
            <HelpIcon className="h-4 w-4 text-secondary-foreground" />{" "}
            <span>What permissions are required for the setup?</span>
          </a>
        </DefaultTooltip>
      </div>
    </div>
  );
};

const RequiredRoles = () => {
  return (
    <div className="flex max-w-md flex-col gap-2 text-left">
      <p>
        Automatic configuration of the integration requires a user with one of
        the following Entra roles assigned:
      </p>
      <ul className="list-disc pl-5">
        <li className="font-bold">Privileged Role Administrator</li>
        <li className="font-bold">Global Administrator</li>
      </ul>

      <p>
        Alternatively, Prelude can automate the setup and configuration of the
        App Registration using the &quot;Application Administrator&quot; role,
        but the integration will not function until a member of the Global
        Administrator or Privileged Role Administrator group provides admin
        consent.
      </p>
    </div>
  );
};
